import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import { useAccount, useConnect, useNetwork, useSwitchNetwork } from "wagmi";
import { VR_RE_IMAGES } from "../../../data/vrRealEstateImages";
import {
  OSHAR_REAL_ESTATE_CHAIN_ID,
  OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
  SEPOLIA_USDC_ADDRESS,
} from "../../../services/web3/contractConfig";
import { getChainLabel, getSwitchToNetworkLabel } from "../../../config/chainEnv";
import {
  approveUSDC,
  isTokenMinted,
  mintWithETH,
  mintWithUSDC,
  readOwnerOf,
  readTokenEthPrice,
  readTokenURI,
  readTokenUsdcPrice,
  readUSDCAllowance,
  readUSDCBalance,
} from "../../../services/web3/osharRealEstateContract";
import { isWrongNetwork } from "../../../services/web3/walletService";
import {
  createOsharCheckoutSession,
  getOsharApiBaseUrl,
  syncOsharWalletMint,
} from "../../../services/osharRealEstateApi";
import { getOsharWalletMintCandidate } from "../../../services/osharRealEstateTiersApi";
import { useAuth } from "../../../context/AuthContext";
import { getAuthToken } from "../../../services/authApi";
import PurchaseLegalAcknowledgment from "../../legal/PurchaseLegalAcknowledgment";
import LegalProductNotice from "../../legal/LegalProductNotice";
import "../../../pages/Library/library.css";
import "./oshar-real-estate-purchase-card.css";
import {
  formatOsharTierUsd,
  getOsharTierRemainingCount,
  getOsharTierMaxSelectablePerCheckout,
  clampOsharPurchaseQuantity,
  getOsharTierSlugForCheckout,
} from "../../../lib/osharRealEstatePurchaseCardAdapter";

const OSHAR_NETWORK_LABEL = getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID);
const SWITCH_OSHAR_NETWORK_LABEL = getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID);

const USDC_DECIMALS = 6;

const compactAddress = (value) => {
  if (!value) return "Not Connected";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const formatEthDisplay = (ethPriceWei, isLoading) => {
  if (isLoading) return "Loading...";
  if (!ethPriceWei || ethPriceWei.isZero()) return "ETH Price Not Set";
  return `${ethers.utils.formatEther(ethPriceWei)} ETH`;
};

const normalizeWeb3Error = (error) => {
  const raw = error?.shortMessage || error?.reason || error?.message || "";
  const lowered = String(raw).toLowerCase();
  if (lowered.includes("cannot estimate gas") || lowered.includes("execution reverted")) {
    return "Transaction reverted. Check token availability, price, allowance, and selected network.";
  }
  if (lowered.includes("missing revert data")) {
    return "Contract call failed. Confirm the configured contract address and token settings.";
  }
  if (lowered.includes("user rejected")) {
    return "Transaction was cancelled in wallet.";
  }
  return raw || "Transaction failed.";
};

const getPaymentAvailability = ({
  usdcPriceRaw,
  needsUsdcApproval,
  hasInsufficientUsdc,
  isConnected,
  wrongNetwork,
  minted,
  supply,
}) => {
  if (usdcPriceRaw == null) return { label: "USDC Price Not Set", enabled: false, action: "unavailable" };
  if (minted) {
    return { label: "Minted", enabled: false, action: "none" };
  }
  if (!isConnected) return { label: "Connect Wallet", enabled: false, action: "none" };
  if (wrongNetwork) return { label: SWITCH_OSHAR_NETWORK_LABEL, enabled: false, action: "none" };
  if (hasInsufficientUsdc) return { label: "Insufficient USDC", enabled: false, action: "none" };
  if (needsUsdcApproval) return { label: "Approve USDC", enabled: true, action: "approve" };
  return { label: "Mint with USDC", enabled: true, action: "mint" };
};

const getWalletPrimaryAction = ({
  isConnected,
  wrongNetwork,
  minted,
  hasTokenId,
  ethPriceWei,
  loading,
  isConnectingWallet,
  isSwitchingNetwork,
  isMintingEth,
  syncPhase,
  walletMintReady,
  walletMintBlockers,
  hasInjectedWallet,
}) => {
  if (loading) return { label: "Loading…", disabled: true, action: "none" };
  if (isConnectingWallet) return { label: "Waiting for MetaMask…", disabled: true, action: "none" };
  if (isSwitchingNetwork) return { label: `Switching to ${OSHAR_NETWORK_LABEL}…`, disabled: true, action: "none" };
  if (isMintingEth) return { label: `Minting on ${OSHAR_NETWORK_LABEL}…`, disabled: true, action: "none" };
  if (syncPhase === "syncing") return { label: "Syncing mint…", disabled: true, action: "none" };
  if (minted) {
    return { label: "Minted — View Dashboard", disabled: true, action: "none" };
  }
  if (!isConnected) {
    if (!hasInjectedWallet) {
      return { label: "MetaMask required", disabled: true, action: "none" };
    }
    return { label: "Connect Wallet", disabled: false, action: "connect" };
  }
  if (wrongNetwork) return { label: SWITCH_OSHAR_NETWORK_LABEL, disabled: false, action: "switch" };
  if (!hasTokenId || walletMintReady === false) {
    return { label: "Wallet mint unavailable", disabled: true, action: "none", blockers: walletMintBlockers };
  }
  if (!ethPriceWei || ethPriceWei.isZero()) {
    return { label: "Token price setup pending", disabled: true, action: "none" };
  }
  return { label: "Mint 1 with Wallet", disabled: false, action: "mintEth" };
};

/**
 * Ordered reasons why Pay with Card cannot run (first entry is shown in UI).
 * Mirrors `canReserveCard` / inventory rules — keep in sync when changing checkout gates.
 */
function collectStripeDisabledReasons({
  isReservingCard,
  modalStatus,
  mintState,
  legalAck,
  cardAuthReady,
  canReserveCard,
  remainingM,
  item,
  tierSlugForItem,
  cardQty,
  maxSelM,
}) {
  const reasons = [];
  const supplyNum = Number(item?.supply);
  const isMultiSupplyTier = Number.isFinite(supplyNum) && supplyNum > 1;

  if (isReservingCard) reasons.push("Preparing secure checkout — please wait.");
  if (modalStatus === "Reserved") reasons.push("This property tier is reserved.");
  if (modalStatus === "Unavailable") reasons.push("This property tier is unavailable.");
  /** Catalog tiers (supply > 1): Sepolia sample slot can be minted without blocking Stripe inventory checkout. */
  if (!isMultiSupplyTier && (mintState.minted || modalStatus === "Minted")) {
    reasons.push(`This representative token is already minted on ${OSHAR_NETWORK_LABEL}.`);
  }
  if (!legalAck?.vrPurchaseOk) reasons.push("Please accept both purchase terms.");
  if (!cardAuthReady) reasons.push("Please log in again to purchase.");
  if (!tierSlugForItem) reasons.push("This property tier is unavailable.");
  const qty = clampOsharPurchaseQuantity(cardQty, maxSelM);
  if (!Number.isInteger(qty) || qty < 1) reasons.push("Select a valid quantity.");
  if (remainingM < 1) reasons.push("Sold Out.");
  if (item?.checkoutEnabled === false) reasons.push("Checkout disabled for this tier.");
  if (item?.stripeFulfillmentReady === false) {
    reasons.push("Checkout temporarily unavailable — fulfillment setup pending.");
  }
  if (item?.priceLabel === "TBD") reasons.push("Pricing not confirmed for card checkout.");
  if (!canReserveCard && reasons.length === 0) {
    reasons.push(item?.disabledReason || "Card checkout is not available for this listing.");
  }
  return reasons;
}

/** First Stripe block reason, or null when Pay with Card may proceed to API/token checks. */
function getStripeInteractionBlock(opts) {
  const r = collectStripeDisabledReasons(opts);
  return r.length ? r[0] : null;
}

/** Why primary ETH / connect / switch cannot proceed (null = run onPrimaryAction). */
function getEthWalletInteractionBlock({ primaryAction, legalAck }) {
  if (primaryAction.action === "mintEth" && !legalAck?.vrPurchaseOk) {
    return "Please accept both purchase terms.";
  }
  if (!primaryAction.disabled) return null;
  const { label } = primaryAction;
  if (label === "Loading…" || label === "Loading...") return "Loading wallet mint slot for this tier…";
  if (label.includes("Waiting for MetaMask") || label.includes("Switching to")) {
    return "Wallet is connecting or switching networks…";
  }
  if (label.includes("Minting on")) return "Mint transaction in progress…";
  if (label.includes("Syncing mint")) return "Verifying mint with Treydora…";
  if (label.includes("Minted")) return `This unit is already minted on ${OSHAR_NETWORK_LABEL}.`;
  if (label === "Wallet mint unavailable") {
    const b = primaryAction?.blockers;
    if (Array.isArray(b) && b.length && typeof b[0] === "string" && b[0].trim()) return b[0].trim();
    return "No wallet-mintable token is available for this tier right now.";
  }
  if (label === "MetaMask required") return "Install MetaMask (or another injected wallet) for this browser.";
  if (label === "Token price setup pending") return "On-chain ETH price for this token is zero or not configured.";
  if (label === "Connect Wallet") return null;
  if (label === SWITCH_OSHAR_NETWORK_LABEL) return null;
  return `${label} — wallet action cannot run yet.`;
}

function getUsdcWalletInteractionBlock({ usdcAction, legalAck }) {
  if ((usdcAction.action === "approve" || usdcAction.action === "mint") && !legalAck?.vrPurchaseOk) {
    return "Please accept both purchase terms.";
  }
  if (!usdcAction.enabled) {
    const { label } = usdcAction;
    if (label === "Connect Wallet") return "Connect wallet.";
    if (label === SWITCH_OSHAR_NETWORK_LABEL) return SWITCH_OSHAR_NETWORK_LABEL;
    if (label === "USDC Price Not Set") return "USDC price not set for this token.";
    if (label === "Insufficient USDC") return "Insufficient USDC.";
    if (label?.includes("Minted") || label?.includes("minted")) return `This unit is already minted on ${OSHAR_NETWORK_LABEL}.`;
    if (label === "Reserved" || label === "Unavailable") return label;
    return `${label} — USDC action cannot run yet.`;
  }
  return null;
}

const OsharRealEstatePurchaseCard = ({ item, onClose, onAuthRequired }) => {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isLoading: isConnectingWallet } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork, isLoading: isSwitchingNetwork } = useSwitchNetwork();
  const [mintState, setMintState] = useState({
    loading: false,
    minted: false,
    owner: "",
    tokenUri: "",
    ethPriceWei: null,
    usdcPriceUnits: null,
    usdcBalanceRaw: null,
    usdcAllowanceRaw: null,
    mintingEth: false,
    mintingUsdc: false,
    checkingChainStatus: false,
    error: "",
  });
  const [txState, setTxState] = useState({ type: "", message: "" });
  const [isApprovingUsdc, setIsApprovingUsdc] = useState(false);
  const [isReservingCard, setIsReservingCard] = useState(false);
  const [reserveMessage, setReserveMessage] = useState("");
  const [cardQty, setCardQty] = useState(1);
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const showPurchaseDebug = searchParams.get("debugPurchase") === "1";
  const [purchaseDebug, setPurchaseDebug] = useState({
    lastStripeMouseDown: "",
    lastStripeClick: "",
    lastWalletMouseDown: "",
    lastWalletClick: "",
    lastStripeHandler: "",
    lastWalletHandler: "",
    stripeEarlyExit: "",
    walletEarlyExit: "",
    stripeRequestFired: "no",
    lastStripeHttpStatus: "—",
  });
  const [legalAck, setLegalAck] = useState({
    libraryDigitalOk: false,
    libraryNftOk: false,
    vrPurchaseOk: false,
  });

  useEffect(() => {
    setCardQty(1);
    setReserveMessage("");
    setSyncPhase("idle");
    setWalletCandidateNonce(0);
    setPurchaseDebug({
      lastStripeMouseDown: "",
      lastStripeClick: "",
      lastWalletMouseDown: "",
      lastWalletClick: "",
      lastStripeHandler: "",
      lastWalletHandler: "",
      stripeEarlyExit: "",
      walletEarlyExit: "",
      stripeRequestFired: "no",
      lastStripeHttpStatus: "—",
    });
  }, [item?.id]);

  const [walletCandidate, setWalletCandidate] = useState(null);
  const [walletCandidateLoading, setWalletCandidateLoading] = useState(false);
  const [walletCandidateNonce, setWalletCandidateNonce] = useState(0);
  const [syncPhase, setSyncPhase] = useState("idle");

  const tierSlugForItem = getOsharTierSlugForCheckout(item);

  useEffect(() => {
    let cancelled = false;
    if (!tierSlugForItem) {
      setWalletCandidate(null);
      setWalletCandidateLoading(false);
      return;
    }
    setWalletCandidateLoading(true);
    setWalletCandidate(null);
    (async () => {
      const c = await getOsharWalletMintCandidate(tierSlugForItem);
      if (cancelled) return;
      setWalletCandidate(c);
      setWalletCandidateLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tierSlugForItem, item?.id, walletCandidateNonce]);

  const resolvedWalletTokenId = useMemo(() => {
    if (walletCandidateLoading) return null;
    if (
      walletCandidate?.ready &&
      walletCandidate.tokenId != null &&
      Number.isFinite(Number(walletCandidate.tokenId))
    ) {
      return Math.floor(Number(walletCandidate.tokenId));
    }
    return null;
  }, [walletCandidate, walletCandidateLoading]);

  const hasInjectedWallet = typeof window !== "undefined" && Boolean(window.ethereum);

  const tokenId = resolvedWalletTokenId;
  const hasTokenId = resolvedWalletTokenId != null && Number.isInteger(resolvedWalletTokenId) && resolvedWalletTokenId >= 0;
  const wrongNetwork = isWrongNetwork(chain?.id);
  const usdcPriceRaw = Number.isFinite(item?.usdcPrice) ? item.usdcPrice : null;

  useEffect(() => {
    let mounted = true;
    const loadState = async () => {
      if (!hasTokenId) {
        setMintState((prev) => ({
          ...prev,
          loading: false,
          checkingChainStatus: false,
          minted: false,
          owner: "",
          tokenUri: "",
          ethPriceWei: null,
          usdcPriceUnits: null,
          error: "",
        }));
        return;
      }
      setMintState((prev) => ({ ...prev, loading: true, checkingChainStatus: true, error: "" }));
      try {
        const [ethPriceWei, minted] = await Promise.all([
          readTokenEthPrice(tokenId),
          isTokenMinted(tokenId),
        ]);
        const usdcPriceUnits = await readTokenUsdcPrice(tokenId);
        let owner = "";
        let tokenUri = "";
        if (minted) {
          [owner, tokenUri] = await Promise.all([readOwnerOf(tokenId), readTokenURI(tokenId)]);
        }

        let usdcBalanceRaw = null;
        let usdcAllowanceRaw = null;
        if (address) {
          [usdcBalanceRaw, usdcAllowanceRaw] = await Promise.all([
            readUSDCBalance(address),
            readUSDCAllowance(address, OSHAR_REAL_ESTATE_CONTRACT_ADDRESS),
          ]);
        }

        if (!mounted) return;
        setMintState((prev) => ({
          ...prev,
          loading: false,
          checkingChainStatus: false,
          minted,
          owner,
          tokenUri,
          ethPriceWei,
          usdcPriceUnits,
          usdcBalanceRaw,
          usdcAllowanceRaw,
        }));
      } catch (error) {
        if (!mounted) return;
        setMintState((prev) => ({
          ...prev,
          loading: false,
          checkingChainStatus: false,
          error: normalizeWeb3Error(error) || "Unable to read NFT state.",
        }));
      }
    };

    loadState();
    return () => {
      mounted = false;
    };
  }, [address, hasTokenId, tokenId]);

  const refreshTokenState = async () => {
    if (!hasTokenId) return;
    setMintState((prev) => ({ ...prev, loading: true }));
    try {
      const [ethPriceWei, usdcPriceUnits, minted] = await Promise.all([
        readTokenEthPrice(tokenId),
        readTokenUsdcPrice(tokenId),
        isTokenMinted(tokenId),
      ]);
      let owner = "";
      let tokenUri = "";
      if (minted) {
        [owner, tokenUri] = await Promise.all([readOwnerOf(tokenId), readTokenURI(tokenId)]);
      }
      let usdcBalanceRaw = null;
      let usdcAllowanceRaw = null;
      if (address) {
        [usdcBalanceRaw, usdcAllowanceRaw] = await Promise.all([
          readUSDCBalance(address),
          readUSDCAllowance(address, OSHAR_REAL_ESTATE_CONTRACT_ADDRESS),
        ]);
      }
      setMintState((prev) => ({
        ...prev,
        loading: false,
        minted,
        owner,
        tokenUri,
        ethPriceWei,
        usdcPriceUnits,
        usdcBalanceRaw,
        usdcAllowanceRaw,
      }));
    } catch (error) {
      setMintState((prev) => ({
        ...prev,
        loading: false,
        error: normalizeWeb3Error(error) || "Unable to refresh token state.",
      }));
    }
  };

  const title = item?.title || item?.name;
  const imgKey = item?.imageKey;
  const img = imgKey ? VR_RE_IMAGES[imgKey] : null;
  const district = item?.district || "Oshar District";
  const description = item?.description || item?.shortDescription || item?.useCase;
  const utilityList = item?.utilityList || [];
  const statusOverride = item?.statusOverride || null;
  const modalStatus = mintState.minted
    ? "Minted"
    : statusOverride === "reserved"
      ? "Reserved"
      : statusOverride === "unavailable"
        ? "Unavailable"
        : "Available";

  const remainingM = useMemo(() => getOsharTierRemainingCount(item), [item]);

  const maxSelM = useMemo(() => getOsharTierMaxSelectablePerCheckout(item, remainingM), [item, remainingM]);

  const supplyNum = Number(item?.supply);
  const isMultiSupplyTier = Number.isFinite(supplyNum) && supplyNum > 1;

  const canReserveCard = useMemo(() => {
    if (remainingM < 1) return false;
    if (item?.priceLabel === "TBD") return false;
    if (item?.checkoutEnabled === false) return false;
    if (statusOverride === "reserved" || statusOverride === "unavailable") return false;
    if (modalStatus === "Reserved" || modalStatus === "Unavailable") return false;
    /** Single-slot tiers: on-chain mint blocks card. Multi-supply: Stripe sells catalog inventory separately. */
    if (!isMultiSupplyTier && mintState.minted) return false;
    if (!isMultiSupplyTier && modalStatus === "Minted") return false;
    if (item?.checkoutEnabled === true) return true;
    return item?.usdcPrice != null || (Number(item?.price) > 0 && item?.priceLabel !== "TBD");
  }, [mintState.minted, modalStatus, remainingM, item, statusOverride, isMultiSupplyTier]);

  const cardAuthReady = Boolean(getAuthToken());
  const authTokenPresent = cardAuthReady;

  const stripeDisabledReasons = useMemo(
    () =>
      collectStripeDisabledReasons({
        isReservingCard,
        modalStatus,
        mintState,
        legalAck,
        cardAuthReady,
        canReserveCard,
        remainingM,
        item,
        tierSlugForItem,
        cardQty,
        maxSelM,
      }),
    [
      isReservingCard,
      modalStatus,
      mintState,
      legalAck,
      cardAuthReady,
      canReserveCard,
      remainingM,
      item,
      tierSlugForItem,
      cardQty,
      maxSelM,
    ]
  );
  const stripeDisabledReason = stripeDisabledReasons[0] || "";
  const isStripeDisabled = stripeDisabledReasons.length > 0;

  useEffect(() => {
    setCardQty((q) => clampOsharPurchaseQuantity(q, maxSelM));
  }, [maxSelM]);
  const ethPriceDisplay = mintState.ethPriceWei
    ? formatEthDisplay(mintState.ethPriceWei, mintState.loading)
    : formatEthDisplay(mintState.ethPriceWei, mintState.loading);
  const effectiveUsdcPriceUnits =
    mintState.usdcPriceUnits != null
      ? mintState.usdcPriceUnits
      : usdcPriceRaw != null
        ? ethers.utils.parseUnits(String(usdcPriceRaw), USDC_DECIMALS)
        : null;
  const usdcPriceDisplay = effectiveUsdcPriceUnits
    ? `${Number(ethers.utils.formatUnits(effectiveUsdcPriceUnits, USDC_DECIMALS)).toLocaleString()} USDC`
    : mintState.loading
      ? "Loading..."
      : "USDC Price Not Set";
  const usdcBalance = mintState.usdcBalanceRaw
    ? Number(ethers.utils.formatUnits(mintState.usdcBalanceRaw, USDC_DECIMALS))
    : null;
  const usdcAllowance = mintState.usdcAllowanceRaw
    ? Number(ethers.utils.formatUnits(mintState.usdcAllowanceRaw, USDC_DECIMALS))
    : null;
  const usdcPriceValue = effectiveUsdcPriceUnits
    ? Number(ethers.utils.formatUnits(effectiveUsdcPriceUnits, USDC_DECIMALS))
    : null;
  const hasInsufficientUsdc = usdcPriceValue != null && usdcBalance != null && usdcBalance < usdcPriceValue;
  const needsUsdcApproval = usdcPriceValue != null && usdcAllowance != null && usdcAllowance < usdcPriceValue;

  const walletReadLoading = walletCandidateLoading || (hasTokenId && (mintState.loading || mintState.checkingChainStatus));

  const primaryAction = useMemo(
    () =>
      getWalletPrimaryAction({
        isConnected,
        wrongNetwork,
        minted: mintState.minted,
        hasTokenId,
        ethPriceWei: mintState.ethPriceWei,
        loading: walletReadLoading,
        isConnectingWallet,
        isSwitchingNetwork,
        isMintingEth: mintState.mintingEth,
        syncPhase,
        walletMintReady: walletCandidate?.ready,
        walletMintBlockers: walletCandidate?.blockers,
        hasInjectedWallet,
      }),
    [
      hasTokenId,
      isConnected,
      hasInjectedWallet,
      isConnectingWallet,
      isSwitchingNetwork,
      mintState.ethPriceWei,
      walletReadLoading,
      mintState.minted,
      mintState.mintingEth,
      wrongNetwork,
      syncPhase,
      walletCandidate?.ready,
      walletCandidate?.blockers,
    ]
  );

  const usdcAction = useMemo(
    () =>
      getPaymentAvailability({
        usdcPriceRaw: usdcPriceValue,
        needsUsdcApproval,
        hasInsufficientUsdc,
        isConnected,
        wrongNetwork: wrongNetwork,
        minted: mintState.minted,
        supply: item?.supply,
      }),
    [
      hasInsufficientUsdc,
      isConnected,
      mintState.minted,
      needsUsdcApproval,
      usdcPriceValue,
      wrongNetwork,
      item?.supply,
    ]
  );

  const ethWalletBlock = useMemo(
    () => getEthWalletInteractionBlock({ primaryAction, legalAck }),
    [primaryAction, legalAck]
  );

  const usdcWalletBlock = useMemo(
    () => getUsdcWalletInteractionBlock({ usdcAction, legalAck }),
    [usdcAction, legalAck]
  );

  const onMintWithEth = async () => {
    setTxState({ type: "info", message: "Wallet handler reached…" });
    if (!String(OSHAR_REAL_ESTATE_CONTRACT_ADDRESS || "").trim()) {
      const m = "Missing contract address.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!legalAck.vrPurchaseOk) {
      const m = "Please accept both purchase terms.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!isConnected) {
      const m = "Connect wallet.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!hasTokenId) {
      const m = "Missing token ID for this listing.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (mintState.minted) {
      const m = `This unit is already minted on ${OSHAR_NETWORK_LABEL}.`;
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!walletCandidate?.ready) {
      const m =
        (Array.isArray(walletCandidate?.blockers) && walletCandidate.blockers[0]) ||
        "Wallet mint is not available for this tier.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (wrongNetwork) {
      setTxState({ type: "info", message: `${SWITCH_OSHAR_NETWORK_LABEL} — opening network switch…` });
      switchNetwork?.(OSHAR_REAL_ESTATE_CHAIN_ID);
      return;
    }
    if (!mintState.ethPriceWei || mintState.ethPriceWei.isZero()) {
      const m = "ETH price is not available on-chain for this token.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    setMintState((prev) => ({ ...prev, mintingEth: true, error: "" }));
    setTxState({ type: "info", message: "Waiting for MetaMask…" });
    try {
      const { txHash } = await mintWithETH(tokenId, mintState.ethPriceWei);
      setTxState({ type: "info", message: "Syncing mint…" });
      try {
        setSyncPhase("syncing");
        if (isAuthenticated) {
          await syncOsharWalletMint({ txHash, chainId: OSHAR_REAL_ESTATE_CHAIN_ID, tokenId });
        }
      } catch (syncErr) {
        const owner = await readOwnerOf(tokenId);
        setMintState((prev) => ({
          ...prev,
          mintingEth: false,
          minted: true,
          owner,
        }));
        await refreshTokenState();
        const raw = syncErr?.message || "Unknown error";
        const linkMsg =
          raw.includes("Link this wallet") || raw.includes("Gateway wallet linking")
            ? raw
            : `On-chain mint succeeded, but Treydora could not record your deed: ${raw}. Link this wallet in Gateway settings before syncing, or use My Oshar Access → Sync wallet deeds after linking.`;
        setTxState({
          type: "error",
          message: linkMsg,
        });
        return;
      } finally {
        setSyncPhase("idle");
      }
      const owner = await readOwnerOf(tokenId);
      setMintState((prev) => ({
        ...prev,
        mintingEth: false,
        minted: true,
        owner,
      }));
      await refreshTokenState();
      setWalletCandidateNonce((n) => n + 1);
      if (isAuthenticated) {
        setTxState({
          type: "success",
          message: (
            <span>
              Minted —{" "}
              <Link to="/dashboard#oshar-access" className="oshar-re-modal__inline-link">
                View Dashboard
              </Link>
            </span>
          ),
        });
      } else {
        setTxState({
          type: "success",
          message:
            `Minted on ${OSHAR_NETWORK_LABEL}. Log in to Treydora and use Gateway → My Oshar Access → Sync wallet deeds to attach this NFT to your dashboard.`,
        });
      }
    } catch (error) {
      setMintState((prev) => ({
        ...prev,
        mintingEth: false,
        error: normalizeWeb3Error(error) || "ETH mint failed.",
      }));
      setTxState({
        type: "error",
        message: normalizeWeb3Error(error) || "Mint failed. Please try again.",
      });
    }
  };

  const onConnectWallet = () => {
    setTxState({ type: "info", message: "Wallet handler reached…" });
    if (typeof window !== "undefined" && !window.ethereum) {
      setTxState({
        type: "error",
        message: "MetaMask (or another injected Ethereum wallet) is required. Install MetaMask for this browser.",
      });
      setPurchaseDebug((d) => ({
        ...d,
        walletEarlyExit: "MetaMask required — window.ethereum missing.",
      }));
      return;
    }
    if (!connectors.length) {
      setTxState({ type: "error", message: "No wallet connector is available in this browser." });
      return;
    }
    connect({ connector: connectors[0] });
  };

  const onSwitchNetwork = () => {
    setTxState({ type: "info", message: `Wallet handler reached… Switching to ${OSHAR_NETWORK_LABEL}…` });
    switchNetwork?.(OSHAR_REAL_ESTATE_CHAIN_ID);
  };

  const onApproveUsdc = async () => {
    setTxState({ type: "info", message: "Wallet handler reached…" });
    if (!legalAck.vrPurchaseOk) {
      const m = "Please accept both purchase terms.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!address || !effectiveUsdcPriceUnits) {
      const m = !address ? "Connect wallet." : "USDC price not available for approval.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    try {
      setIsApprovingUsdc(true);
      setTxState({ type: "info", message: "USDC approval pending..." });
      const { txHash } = await approveUSDC(OSHAR_REAL_ESTATE_CONTRACT_ADDRESS, effectiveUsdcPriceUnits);
      setTxState({ type: "success", message: "USDC approved. You can now mint with USDC." });
      await refreshTokenState();
      setTxState({ type: "success", message: `USDC approved. Tx: ${txHash}` });
    } catch (error) {
      setTxState({
        type: "error",
        message: normalizeWeb3Error(error) || "USDC approval failed.",
      });
    } finally {
      setIsApprovingUsdc(false);
    }
  };

  const onMintWithUsdc = async () => {
    setTxState({ type: "info", message: "Wallet handler reached…" });
    if (!String(OSHAR_REAL_ESTATE_CONTRACT_ADDRESS || "").trim()) {
      const m = "Missing contract address.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!legalAck.vrPurchaseOk) {
      const m = "Please accept both purchase terms.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!hasTokenId) {
      const m = "Missing token ID for this listing.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (mintState.minted) {
      const m = `This unit is already minted on ${OSHAR_NETWORK_LABEL}.`;
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    if (!walletCandidate?.ready) {
      const m =
        (Array.isArray(walletCandidate?.blockers) && walletCandidate.blockers[0]) ||
        "Wallet mint is not available for this tier.";
      setTxState({ type: "error", message: m });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: m }));
      return;
    }
    try {
      setMintState((prev) => ({ ...prev, mintingUsdc: true, error: "" }));
      setTxState({ type: "info", message: "Waiting for MetaMask…" });
      const { txHash } = await mintWithUSDC(tokenId);
      setTxState({ type: "info", message: "Syncing mint…" });
      try {
        setSyncPhase("syncing");
        if (isAuthenticated) {
          await syncOsharWalletMint({ txHash, chainId: OSHAR_REAL_ESTATE_CHAIN_ID, tokenId });
        }
      } catch (syncErr) {
        const owner = await readOwnerOf(tokenId);
        setMintState((prev) => ({
          ...prev,
          mintingUsdc: false,
          minted: true,
          owner,
        }));
        await refreshTokenState();
        const raw = syncErr?.message || "Unknown error";
        const linkMsg =
          raw.includes("Link this wallet") || raw.includes("Gateway wallet linking")
            ? raw
            : `On-chain mint succeeded, but Treydora could not record your deed: ${raw}. Link this wallet in Gateway settings before syncing, or use My Oshar Access → Sync wallet deeds after linking.`;
        setTxState({
          type: "error",
          message: linkMsg,
        });
        return;
      } finally {
        setSyncPhase("idle");
      }
      const owner = await readOwnerOf(tokenId);
      setMintState((prev) => ({
        ...prev,
        mintingUsdc: false,
        minted: true,
        owner,
      }));
      await refreshTokenState();
      setWalletCandidateNonce((n) => n + 1);
      if (isAuthenticated) {
        setTxState({
          type: "success",
          message: (
            <span>
              Minted —{" "}
              <Link to="/dashboard#oshar-access" className="oshar-re-modal__inline-link">
                View Dashboard
              </Link>
            </span>
          ),
        });
      } else {
        setTxState({
          type: "success",
          message:
            `Minted on ${OSHAR_NETWORK_LABEL}. Log in to Treydora and use Gateway → My Oshar Access → Sync wallet deeds to attach this NFT to your dashboard.`,
        });
      }
    } catch (error) {
      setMintState((prev) => ({
        ...prev,
        mintingUsdc: false,
        error: normalizeWeb3Error(error) || "USDC mint failed.",
      }));
      setTxState({
        type: "error",
        message: normalizeWeb3Error(error) || "USDC mint failed.",
      });
    }
  };

  const onPrimaryAction = () => {
    if (primaryAction.action === "connect") {
      onConnectWallet();
      return;
    }
    if (primaryAction.action === "switch") {
      onSwitchNetwork();
      return;
    }
    if (primaryAction.action === "mintEth") {
      onMintWithEth();
    }
  };

  const handlePrimaryWalletButtonMouseDown = () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastWalletMouseDown: `${Date.now()}:${primaryAction.label}`,
    }));
  };

  const handlePrimaryWalletButtonClick = () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastWalletClick: `${Date.now()}:${primaryAction.label}`,
      lastWalletHandler: "primaryWalletButton",
      walletEarlyExit: "",
    }));
    if (ethWalletBlock) {
      setTxState({ type: "error", message: ethWalletBlock });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: ethWalletBlock }));
      return;
    }
    onPrimaryAction();
  };

  const handleUsdcWalletButtonMouseDown = () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastWalletMouseDown: `${Date.now()}:usdc:${usdcAction.label}`,
    }));
  };

  const handleUsdcWalletButtonClick = () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastWalletClick: `${Date.now()}:usdc:${usdcAction.label}`,
      lastWalletHandler: "usdcWalletButton",
      walletEarlyExit: "",
    }));
    if (usdcWalletBlock) {
      setTxState({ type: "error", message: usdcWalletBlock });
      setPurchaseDebug((d) => ({ ...d, walletEarlyExit: usdcWalletBlock }));
      return;
    }
    if (usdcAction.action === "approve") onApproveUsdc();
    else if (usdcAction.action === "mint") onMintWithUsdc();
  };

  /** Use stored Bearer token so Pay with Card matches `createOsharCheckoutSession`. */
  const onReserveWithCard = async () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastStripeHandler: "onReserveWithCard",
      stripeEarlyExit: "",
    }));

    const preStripe = getStripeInteractionBlock({
      isReservingCard,
      modalStatus,
      mintState,
      legalAck,
      cardAuthReady,
      canReserveCard,
      remainingM,
      item,
      tierSlugForItem,
      cardQty,
      maxSelM,
    });
    if (preStripe) {
      if (preStripe === "Please log in again to purchase.") {
        onAuthRequired?.(item);
      }
      setReserveMessage(preStripe);
      setPurchaseDebug((d) => ({ ...d, stripeEarlyExit: preStripe }));
      return;
    }

    const apiBase = String(getOsharApiBaseUrl() || "").trim();
    if (!apiBase) {
      const m = "Checkout service unavailable.";
      setReserveMessage(m);
      setPurchaseDebug((d) => ({ ...d, stripeEarlyExit: m }));
      return;
    }

    if (!tierSlugForItem) {
      const m = "This property tier is unavailable.";
      setReserveMessage(m);
      setPurchaseDebug((d) => ({ ...d, stripeEarlyExit: m }));
      return;
    }

    const qty = clampOsharPurchaseQuantity(cardQty, maxSelM);
    if (!Number.isInteger(qty) || qty < 1) {
      const m = "Select a valid quantity.";
      setReserveMessage(m);
      setPurchaseDebug((d) => ({ ...d, stripeEarlyExit: m }));
      return;
    }

    setIsReservingCard(true);
    setReserveMessage("Preparing secure checkout…");
    setPurchaseDebug((d) => ({
      ...d,
      stripeRequestFired: "yes",
      lastStripeHttpStatus: "pending",
      stripeEarlyExit: "",
    }));
    try {
      const session = await createOsharCheckoutSession({
        tierSlug: tierSlugForItem,
        quantity: qty,
      });
      const url = typeof session?.url === "string" ? session.url.trim() : "";
      if (!url) {
        setReserveMessage("Stripe did not return a checkout URL.");
        setPurchaseDebug((d) => ({
          ...d,
          stripeEarlyExit: "Stripe did not return a checkout URL.",
        }));
        return;
      }
      setReserveMessage("Redirecting to Stripe…");
      setPurchaseDebug((d) => ({ ...d, lastStripeHttpStatus: "200" }));
      window.location.assign(url);
    } catch (error) {
      const msg = error?.message || "Stripe checkout request failed.";
      setReserveMessage(msg);
      setPurchaseDebug((d) => ({
        ...d,
        stripeEarlyExit: msg,
        lastStripeHttpStatus: "error",
      }));
      setIsReservingCard(false);
    }
  };

  const handleStripeCardMouseDown = () => {
    setPurchaseDebug((d) => ({ ...d, lastStripeMouseDown: String(Date.now()) }));
  };

  const handleStripeCardClick = () => {
    setPurchaseDebug((d) => ({
      ...d,
      lastStripeClick: String(Date.now()),
      lastStripeHandler: "stripeButtonClick",
    }));
    void onReserveWithCard();
  };

  const payCardLabel = useMemo(() => {
    if (remainingM < 1) return "Sold Out";
    if (isReservingCard) {
      if (reserveMessage === "Redirecting to Stripe…") return "Redirecting to Stripe…";
      return "Preparing secure checkout…";
    }
    if (!cardAuthReady) return "Please log in again to purchase";
    if (!canReserveCard) {
      return item?.disabledReason || "Card checkout is not available for this property";
    }
    return "Buy Property with Card";
  }, [
    remainingM,
    isReservingCard,
    reserveMessage,
    cardAuthReady,
    canReserveCard,
    item?.disabledReason,
  ]);

  if (!item) return null;

  const qtyEff = clampOsharPurchaseQuantity(cardQty, maxSelM);
  const unitPriceUsd = Number(item?.price) > 0 ? Number(item.price) : null;
  const priceOk = unitPriceUsd != null && item?.priceLabel !== "TBD";
  const cardTotalUsd =
    unitPriceUsd != null && item?.priceLabel !== "TBD"
      ? unitPriceUsd * qtyEff
      : null;
  const qtyBusy = isReservingCard;

  return (
    <div className="oshar-re-purchase-card-root">
      {showPurchaseDebug ? (
        <div className="oshar-re-purchase-debug oshar-re-purchase-debug--panel">
          <strong style={{ color: "#f2e6c4" }}>Purchase debug</strong> — OsharRealEstatePurchaseCard
          <br />
          tierSlug: {tierSlugForItem || "—"} | qty: {qtyEff} | maxPerCheckout (raw): {String(item?.maxPerCheckout ?? "—")} |
          maxSelM: {maxSelM}
          <br />
          remainingM: {remainingM} | item.remaining: {String(item?.remaining ?? "—")} | supply: {String(item?.supply ?? "—")}
          <br />
          checkoutEnabled: {item?.checkoutEnabled === undefined ? "unspecified" : String(item.checkoutEnabled)} | legal
          vrPurchaseOk: {String(legalAck.vrPurchaseOk)}
          <br />
          auth token present: {String(authTokenPresent)} | cardAuthReady: {String(cardAuthReady)} | canReserveCard:{" "}
          {String(canReserveCard)}
          <br />
          card checkout disabled: {String(isStripeDisabled)} | disabled reason: {stripeDisabledReason || "—"}
          <br />
          stripe request fired: {purchaseDebug.stripeRequestFired} | last http: {purchaseDebug.lastStripeHttpStatus}
          <br />
          stripeEarlyExit: {purchaseDebug.stripeEarlyExit || "—"} | isMultiSupplyTier: {String(isMultiSupplyTier)}
          <br />
          lastStripeClick: {purchaseDebug.lastStripeClick || "—"} | lastWalletClick: {purchaseDebug.lastWalletClick || "—"}
        </div>
      ) : null}

      <article
        className="lib-digital-card oshar-re-purchase-lib-card"
        aria-labelledby="oshar-re-purchase-title"
      >
        <div className="lib-digital-card__media">
          <div className="lib-digital-card__frame" aria-hidden>
            <div className="lib-digital-card__frame-glow" />
            {img ? (
              <img className="lib-digital-card__cover-img" src={img} alt="" loading="lazy" decoding="async" />
            ) : (
              <div className="oshar-re-purchase-card__cover-fallback" aria-hidden />
            )}
          </div>
        </div>

        <div className="lib-digital-card__body">
          <ul className="lib-digital-card__badges" aria-label="Property tags">
            <li className="lib-digital-card__badge lib-digital-card__badge--accent">Oshar VR</li>
            <li className="lib-digital-card__badge lib-digital-card__badge--muted">{district}</li>
            {item?.assetType ? (
              <li className="lib-digital-card__badge lib-digital-card__badge--muted">{item.assetType}</li>
            ) : null}
          </ul>

          {mintState.minted ? (
            <p className="oshar-re-devnet-mint-pill" role="status">
              Minted on {OSHAR_NETWORK_LABEL}
            </p>
          ) : null}

          <h3 id="oshar-re-purchase-title" className="lib-digital-card__title lib-serif">
            {title}
          </h3>
          <p className="lib-digital-card__desc">{description || "Digital access and placement rights inside the Oshar city experience."}</p>

          {utilityList.length > 0 ? (
            <ul className="lib-edition__bullets oshar-re-purchase-card__bullets">
              {utilityList.map((utility) => (
                <li key={utility}>{utility}</li>
              ))}
            </ul>
          ) : null}

          <div className="lib-digital-card__commerce">
            <div className="lib-digital-card__price-block">
              <span className="lib-digital-card__price-label">Per property</span>
              <p className="lib-digital-card__price">
                {priceOk && formatOsharTierUsd(unitPriceUsd) ? `${formatOsharTierUsd(unitPriceUsd)} each` : item?.priceLabel || "TBD"}
              </p>
              {remainingM != null ? (
                <p className="lib-sec-desc" style={{ margin: "6px 0 0", fontSize: "0.76rem", opacity: 0.85 }}>
                  <strong>{remainingM.toLocaleString()} remain</strong>
                  {item?.supply != null && Number.isFinite(Number(item.supply))
                    ? ` · tier capacity ${Number(item.supply).toLocaleString()}`
                    : ""}
                </p>
              ) : null}

              {remainingM >= 1 && priceOk ? (
                <div className="lib-nft-qty lib-nft-qty--compact" aria-label="Purchase quantity">
                  <span className="lib-nft-qty__label">Quantity</span>
                  <div className="lib-nft-qty__btns">
                    <button
                      type="button"
                      className="lib-nft-qty__step"
                      aria-label="Decrease quantity"
                      disabled={qtyBusy || qtyEff <= 1}
                      onClick={() =>
                        setCardQty((prev) => clampOsharPurchaseQuantity((Number(prev) || 1) - 1, maxSelM))
                      }
                    >
                      −
                    </button>
                    <span className="lib-nft-qty__value" aria-live="polite">
                      {qtyEff}
                    </span>
                    <button
                      type="button"
                      className="lib-nft-qty__step"
                      aria-label="Increase quantity"
                      disabled={qtyBusy || qtyEff >= maxSelM}
                      onClick={() =>
                        setCardQty((prev) => clampOsharPurchaseQuantity((Number(prev) || 1) + 1, maxSelM))
                      }
                    >
                      +
                    </button>
                  </div>
                  <span className="lib-nft-qty__total">
                    Total:{" "}
                    {cardTotalUsd != null
                      ? `$${cardTotalUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : "—"}
                  </span>
                  <span className="lib-nft-qty__cap">
                    Max {maxSelM} per checkout · card only. Wallet mints one NFT per transaction.
                  </span>
                </div>
              ) : null}
            </div>

            <PurchaseLegalAcknowledgment
              key={String(item?.id || item?.tierSlug || "purchase-card")}
              className="oshar-re-purchase-legal--compact"
              showVrCheckbox
              digitalAccessPurchaseTerms
              onValidityChange={setLegalAck}
            />

            <LegalProductNotice variant="vrRealEstate" />

            {modalStatus === "Reserved" ? (
              <p className="oshar-re-modal__warn" role="status">
                {item?.reservationNote || "This property tier is currently reserved."}
              </p>
            ) : null}
            {modalStatus === "Unavailable" ? (
              <p className="oshar-re-modal__warn" role="status">
                This property tier is currently unavailable.
              </p>
            ) : null}

            {(reserveMessage || isReservingCard) && (
              <p className="oshar-re-modal__reserve-status" role="status" aria-live="polite">
                {reserveMessage}
              </p>
            )}

            <p className="oshar-re-modal__hint oshar-re-purchase-card__card-wallet-note" role="note">
              Card purchases reserve digital access in your Treydora account. Wallet minting, where available, creates
              an on-chain record after confirmation.
            </p>

            <div className="lib-digital-card__actions oshar-re-purchase-card__actions">
              <button type="button" className="oshar-btn-outline oshar-re-btn-sm" onClick={onClose}>
                Close details
              </button>
              {isAuthenticated ? (
                <Link
                  to="/dashboard#oshar-access"
                  className="oshar-btn-outline oshar-re-btn-sm"
                  style={{ textDecoration: "none" }}
                >
                  My Oshar Access
                </Link>
              ) : null}
              <button
                type="button"
                className={`lib-btn lib-btn--gold lib-btn--sm${!isStripeDisabled ? " oshar-re-purchase-card__stripe--ready" : ""}`}
                disabled={isStripeDisabled}
                onMouseDown={handleStripeCardMouseDown}
                onClick={handleStripeCardClick}
              >
                {payCardLabel}
              </button>
            </div>

            {isStripeDisabled ? (
              <p className="oshar-re-modal__hint oshar-re-purchase-card__disabled-reason" role="status">
                {stripeDisabledReason}
              </p>
            ) : null}

            <details
              open
              className="oshar-re-modal__wallet-accordion oshar-re-purchase-card__wallet-details"
            >
              <summary className="oshar-re-modal__wallet-accordion-summary">Mint with crypto wallet ({OSHAR_NETWORK_LABEL})</summary>
              <div className="oshar-re-modal__wallet-accordion-body">
                <p className="oshar-re-modal__wallet-accordion-intro">
                  Pay with card above to reserve multiple units in one checkout. Use MetaMask below to mint{" "}
                  <strong>one</strong> on-chain NFT per transaction on {OSHAR_NETWORK_LABEL}, then Treydora syncs it to your dashboard
                  when your wallet is linked.
                </p>
                <p className="oshar-re-modal__wallet-accordion-intro" style={{ marginTop: 8 }}>
                  Wallet mint slot #{hasTokenId ? tokenId : "—"} · catalog tier #{item?.tokenId != null ? item.tokenId : "—"}{" "}
                  · {modalStatus}
                </p>
                <h4 className="oshar-re-modal__wallet-subheading">On-chain reference</h4>
                <p className="oshar-re-modal__price-line">
                  <span>ETH</span>
                  <strong>{ethPriceDisplay}</strong>
                </p>
                <p className="oshar-re-modal__price-line">
                  <span>USDC</span>
                  <strong>{usdcPriceDisplay}</strong>
                </p>
                {hasInsufficientUsdc && <p className="oshar-re-modal__warn">Insufficient USDC</p>}
                <p className="oshar-re-modal__wallet-line">
                  Wallet: {isConnected ? compactAddress(address) : "Not Connected"}
                </p>
                <p className="oshar-re-modal__wallet-line">
                  Network: {wrongNetwork ? "Wrong Network" : chain?.name || "Not Connected"}
                </p>
                {wrongNetwork && isConnected ? (
                  <p className="oshar-re-modal__warn">Wrong network detected. {SWITCH_OSHAR_NETWORK_LABEL} to continue.</p>
                ) : null}
                <div className="oshar-re-modal__wallet-actions">
                  <div className="oshar-re-modal__wallet-actions-row">
                    <button
                      type="button"
                      className={`${
                        primaryAction.disabled || (!legalAck.vrPurchaseOk && primaryAction.action === "mintEth")
                          ? "oshar-btn-outline"
                          : "oshar-btn-gold"
                      } oshar-re-action-btn oshar-re-modal__wallet-cta${ethWalletBlock ? " oshar-re-action-btn--blocked" : ""}`}
                      onMouseDown={handlePrimaryWalletButtonMouseDown}
                      onClick={handlePrimaryWalletButtonClick}
                      aria-disabled={ethWalletBlock ? "true" : "false"}
                    >
                      {primaryAction.label}
                    </button>
                    {ethWalletBlock ? (
                      <p className="oshar-re-modal__hint oshar-re-modal__hint--tight">{ethWalletBlock}</p>
                    ) : null}
                  </div>
                  {(usdcAction.action === "approve" || usdcAction.action === "mint") && (
                    <div className="oshar-re-modal__wallet-actions-row">
                      <button
                        type="button"
                        className={`oshar-btn-outline oshar-re-action-btn oshar-re-modal__wallet-cta${
                          usdcWalletBlock || isApprovingUsdc || mintState.mintingUsdc
                            ? " oshar-re-action-btn--blocked"
                            : ""
                        }`}
                        disabled={isApprovingUsdc || mintState.mintingUsdc}
                        onMouseDown={handleUsdcWalletButtonMouseDown}
                        onClick={handleUsdcWalletButtonClick}
                        aria-disabled={usdcWalletBlock || isApprovingUsdc || mintState.mintingUsdc ? "true" : "false"}
                      >
                        {isApprovingUsdc
                          ? "Approving USDC..."
                          : mintState.mintingUsdc
                            ? "Minting with USDC..."
                            : usdcAction.label}
                      </button>
                      {(usdcWalletBlock || isApprovingUsdc || mintState.mintingUsdc) && (
                        <p className="oshar-re-modal__hint oshar-re-modal__hint--tight">
                          {usdcWalletBlock ||
                            (isApprovingUsdc ? "Approval in progress…" : "") ||
                            (mintState.mintingUsdc ? "Mint in progress…" : "")}
                        </p>
                      )}
                    </div>
                  )}
                  {usdcAction.action === "unavailable" ? (
                    <p className="oshar-re-modal__rail-note">USDC rail unavailable</p>
                  ) : null}
                  {usdcAction.label === "Insufficient USDC" ? (
                    <p className="oshar-re-modal__rail-note">Insufficient USDC</p>
                  ) : null}
                </div>
                {showPurchaseDebug ? (
                  <p className="oshar-re-modal__hint oshar-re-modal__hint--tight">USDC contract: {SEPOLIA_USDC_ADDRESS}</p>
                ) : null}
                {txState.message ? (
                  <p className={`oshar-re-modal__tx oshar-re-modal__tx--${txState.type || "info"}`}>{txState.message}</p>
                ) : null}
                {showPurchaseDebug && mintState.owner ? (
                  <p className="oshar-re-modal__hint">Current owner: {mintState.owner}</p>
                ) : null}
                {showPurchaseDebug && mintState.tokenUri ? (
                  <p className="oshar-re-modal__hint">Token URI: {mintState.tokenUri}</p>
                ) : null}
                {mintState.checkingChainStatus ? (
                  <p className="oshar-re-modal__hint">Checking chain status...</p>
                ) : null}
                {mintState.loading ? <p className="oshar-re-modal__hint">Loading price and mint state...</p> : null}
                {mintState.error ? <p className="oshar-re-modal__warn">{mintState.error}</p> : null}
                {!hasTokenId ? <p className="oshar-re-modal__warn">Token ID unavailable for this listing.</p> : null}
              </div>
            </details>
          </div>
        </div>
      </article>
    </div>
  );
};

export default OsharRealEstatePurchaseCard;
